import { useSearchParams } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import './MealPlanViewer.css';
import { parseEstDate, getDayOfWeek, getCutoffTime, sortDeliveryDates } from './services/dateUtils';
import { groupDatesByWeek } from './utils/weekUtils';

// Components
import CountdownLoader from './countdownLoader';
import MealPlanHeader from './components/Header/MealPlanHeader';
import { SuccessMessage, ErrorMessage, LockMessage } from './components/Messages/StatusMessages';
import DeliveryDateTabs from './components/DeliveryDateTabs/DeliveryDateTabs';
import ResumeDelivery from './components/SkipDelivery/ResumeDelivery';
import CompactMealCard from './components/MealCard/CompactMealCard';
import ScrollArrows from './components/MealCard/ScrollArrows';
import MealModal from './components/Modal/MealModal';
import PremiumProteinUpgradeModal from './components/Modal/PremiumProteinUpgradeModal';
import SummaryModal from './components/Modal/SummaryModal';

// Hooks
import { useScrollArrows } from './hooks/useScrollArrows';
import { useLockStatus } from './hooks/useLockStatus';
import { useIsDesktop } from './hooks/useMediaQuery';

// Order Summary Components
import { OrderSummarySidebar, FloatingOrderButton } from './components/OrderSummary';

// Services
import * as mealPlanService from './services/mealPlanService';

// Constants
import { MEAL_TYPES, MEAL_PRICES, SUBSCRIPTION_DISCOUNT, PREMIUM_SURCHARGE, BULK_PRICING, getBulkCategory } from './constants/meals';

export default function MealPlanViewer() {
    const [params] = useSearchParams();
    const token = params.get('customer');

    // State
    const [customerDetails, setCustomerDetails] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    // eslint-disable-next-line no-unused-vars
    const [saving, setSaving] = useState(false); // May be used for future batch save loading state
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    // Modal state
    const [selectedMeal, setSelectedMeal] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showPriceConfirm, setShowPriceConfirm] = useState(null);
    const [showAddedBanner, setShowAddedBanner] = useState(false);
    const [showSummaryModal, setShowSummaryModal] = useState(false);

    // Price change state
    const [billingChanges, setBillingChanges] = useState(null);
    const [priorPaymentsByDate, setPriorPaymentsByDate] = useState({});
    const [paymentRecords, setPaymentRecords] = useState({}); // { [date]: [{ amount, stripeSessionId, timestamp }] }

    // Delivery date state
    const [deliveryDates, setDeliveryDates] = useState([]);
    const [selectedDeliveryDate, setSelectedDeliveryDate] = useState(null);
    const [ordersByDeliveryDate, setOrdersByDeliveryDate] = useState({});
    const [deliveryStatusState, setDeliveryStatusState] = useState({});

    // Pending changes state - tracks unsaved changes per delivery date
    const [pendingChanges, setPendingChanges] = useState({});
    // Delivery time overrides per date - { [date]: 'Evening' }
    const [deliveryTimeOverrides, setDeliveryTimeOverrides] = useState({});
    // Structure:
    // {
    //   "2025-01-15": {
    //     isDirty: true,
    //     changes: [{ type: 'ADD_DISH', ... }, { type: 'REPLACE_PROTEIN', ... }, ...],
    //     originalOrders: [...] // snapshot for discard/rollback
    //   }
    // }

    // Refs
    const modalRef = useRef(null);
    const containerRef = useRef(null);

    // Custom hooks
    const { shouldShowArrows, getScrollState, scrollLeft, scrollRight, handleScroll, initializeScrollPositions } = useScrollArrows();
    const isLocked = useLockStatus(selectedDeliveryDate);
    const isDesktop = useIsDesktop();

    // Payment processing state — shown as overlay modal while waiting for webhook
    const [paymentProcessingModal, setPaymentProcessingModal] = useState(null); // null | 'processing' | 'success' | 'failed' | 'timeout'
    const paymentProcessedRef = useRef(false);

    // Effects — skip initial fetch if this is a payment redirect (payment handler will fetch once)
    useEffect(() => {
        if (!token) {
            setError('No customer token provided');
            setLoading(false);
            return;
        }

        const isPaymentRedirect = params.get('payment') === 'success' && params.get('pendingId');
        const isCancelRedirect = params.get('payment') === 'cancelled';

        if (isPaymentRedirect) {
            // Payment redirect — don't fetch yet, let the handler below do it after polling
            handlePaymentSuccess(params.get('pendingId'));
        } else if (isCancelRedirect) {
            // Cancelled — fetch normally but show message
            paymentProcessedRef.current = true;
            fetchMealPlan();
            setError('Payment was cancelled. Your unsaved changes have been discarded.');
            window.history.replaceState({}, '', `/meal-plan?customer=${token}`);
            setTimeout(() => setError(''), 10000);
        } else {
            // Normal page load
            fetchMealPlan();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // Handle successful payment redirect — poll for webhook, then single fetch
    const handlePaymentSuccess = async (pendingId) => {
        if (paymentProcessedRef.current) return;
        paymentProcessedRef.current = true;
        setPaymentProcessingModal('processing');

        // Poll until webhook has processed the pending changes
        let finalStatus = 'timeout';
        const maxAttempts = 30;
        for (let i = 0; i < maxAttempts; i++) {
            try {
                const result = await mealPlanService.checkPendingStatus(pendingId);
                if (result.status === 'completed') { finalStatus = 'completed'; break; }
                if (result.status === 'save_failed') { finalStatus = 'save_failed'; break; }
            } catch (e) { /* ignore polling errors */ }
            await new Promise(resolve => setTimeout(resolve, 1000));
        }

        window.history.replaceState({}, '', `/meal-plan?customer=${token}`);

        // Single fetch — loads the final state
        await fetchMealPlan();

        if (finalStatus === 'completed') {
            setPaymentProcessingModal('success');
        } else if (finalStatus === 'save_failed') {
            setPaymentProcessingModal('failed');
        } else {
            setPaymentProcessingModal('timeout');
        }
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            initializeScrollPositions(MEAL_TYPES);
        }, 100);
        return () => clearTimeout(timeout);
    }, [orders, initializeScrollPositions]);

    useEffect(() => {
        if (customerDetails?.customer?.name) {
            document.title = `Square Fare for ${customerDetails.customer.name}`;
        } else if (!loading) {
            document.title = 'Square Fare Meal Plan';
        }
    }, [customerDetails?.customer?.name, loading]);

    // Update delivery statuses when customerDetails or deliveryDates change
    useEffect(() => {
        updateDeliveryStatuses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerDetails?.subscriptions]);

    // Week-based billing calculation with shared subscription pool
    // Key principle: Weekly subscription is a shared pool across all dates in the week
    // Moving meals within a week = $0 as long as weekly total matches
    // Premium proteins are tracked weekly (counts of premium dinners, premium lunches, etc.)
    const calculateBillingChanges = useCallback(() => {
        if (!customerDetails?.subscriptions || !deliveryDates.length || !ordersByDeliveryDate) return null;

        let totalCharges = 0;
        let totalRefunds = 0;
        let totalDeliveryFee = 0;
        const dates = {};
        const datesWithChanges = [];
        //debug
        //console.log('customerDetails', customerDetails);
        //console.log('ordersByDeliveryDate', ordersByDeliveryDate);

        // Group delivery dates by week e.g. 2026-W07: ['2026-02-12', '2026-02-15']
        // 2026-W08: ['2026-02-16', '2026-02-17', '2026-02-19']
        const datesByWeek = groupDatesByWeek(deliveryDates);
        //console.log('deliveryDates', deliveryDates);

        // Track premium usage per week per meal type
        const premiumUsageByWeek = {}; // { weekKey: { mealType: { subscribed, ordered, hasExceeded } } }

        Object.entries(datesByWeek).forEach(([weekKey, datesInWeek]) => {
            // ===== PASS 1: Build weekly billing structure matching backend logic =====
            // Structure: { mealType: { subscribed: number, ordered: number, premium: { proteinName: quantity }, price: [] } }
            const weeklyMealsByType = {};
            const snacksPerDay = customerDetails.customer?.snacksPerDay || 1;

            // Initialize meal types
            MEAL_TYPES.forEach(mealType => {
                if (mealType !== 'Add On') {
                    weeklyMealsByType[mealType] = {
                        subscribed: 0,
                        ordered: 0,
                        premium: {},
                        price: [],
                        premiumSubscriptions: 0 // Track count of premium subscriptions
                    };
                }
            });
            weeklyMealsByType['Add On'] = {}; // Track by dishId: { dishId: quantity }

            // First, collect all subscriptions for the week
            datesInWeek.forEach(date => {
                const matchingSubscriptions = customerDetails.subscriptions.filter(sub =>
                    sub.nextDeliveryDate === date
                );
                matchingSubscriptions.forEach(sub => {
                    // Normalize to canonical MEAL_TYPES casing (e.g. "Add on" -> "Add On")
                    const mealType = MEAL_TYPES.find(t => t.toLowerCase() === (sub.mealType || '').toLowerCase()) || sub.mealType;
                    if (mealType === 'Add On') return;

                    const mealsIncluded = sub.mealsIncluded || 0;
                    if (!weeklyMealsByType[mealType]) {
                        weeklyMealsByType[mealType] = {
                            subscribed: 0,
                            ordered: 0,
                            premium: {},
                            price: [],
                            premiumSubscriptions: 0
                        };
                    }
                    weeklyMealsByType[mealType].subscribed += mealsIncluded;

                    // Count premium subscriptions (Premium Meat Only Paid)
                    if (sub.tags?.includes('Premium Meat Only Paid')) {
                        weeklyMealsByType[mealType].premiumSubscriptions += mealsIncluded;
                    }
                });
            });

            // Adjust snack pool by snacksPerDay
            // if (weeklyMealsByType['Snack'] && customerDetails.customer?.snacksPerDay) {
            //     weeklyMealsByType['Snack'].regular = weeklyMealsByType['Snack'].regular * customerDetails.customer.snacksPerDay;
            // }

            // Track orders by date for charge allocation and per-order details
            const ordersByDateAndType = {}; // { date: { mealType: { ordered: number, premium: { proteinName: quantity }, price: [], premiumCharge: { proteinName: charge } } } }
            const orderDetailsByDate = {}; // { date: { mealType: [ { recordId, itemName, quantity, price, isIncluded, premiumProtein: { name, charge } } ] } } }
            datesInWeek.forEach(date => {
                ordersByDateAndType[date] = {};
                orderDetailsByDate[date] = {};
                MEAL_TYPES.forEach(mealType => {
                    if (mealType !== 'Add On') {
                        ordersByDateAndType[date][mealType] = {
                            ordered: 0,
                            premium: {},
                            price: [],
                            premiumCharge: {} // Track charge per protein name
                        };
                        orderDetailsByDate[date][mealType] = [];
                    }
                });
                ordersByDateAndType[date]['Add On'] = {}; // Track by dishId
                orderDetailsByDate[date]['Add On'] = [];
            });

            // Then, collect all ordered meals for each date
            datesInWeek.forEach(date => {
                const ordersForDate = ordersByDeliveryDate[date] || [];

                ordersForDate.forEach(order => {
                    // Normalize meal type to canonical MEAL_TYPES casing (e.g. "Add on" -> "Add On")
                    const rawMeal = order.meal || 'Unknown';
                    const mealType = MEAL_TYPES.find(t => t.toLowerCase() === rawMeal.toLowerCase()) || rawMeal;
                    const quantity = order.quantity || 0;
                    const dishId = order.dishId;

                    if (mealType === 'Add On' && quantity > 0) {
                        // Track add-ons by dishId
                        if (!weeklyMealsByType['Add On'][dishId]) {
                            weeklyMealsByType['Add On'][dishId] = 0;
                        }
                        weeklyMealsByType['Add On'][dishId] += quantity;

                        if (!ordersByDateAndType[date]['Add On'][dishId]) {
                            ordersByDateAndType[date]['Add On'][dishId] = 0;
                        }
                        ordersByDateAndType[date]['Add On'][dishId] += quantity;

                        // Store order details for add-ons
                        const price = order.price || 0;
                        orderDetailsByDate[date]['Add On'].push({
                            recordId: order.recordId,
                            dishId: dishId,
                            itemName: order.itemName,
                            quantity: quantity,
                            price: price, // Add-ons are never included
                            isIncluded: false,
                            premiumProtein: null // Add-ons don't have premium proteins
                        });
                        return;
                    }

                    if (order.isOrdered && quantity > 0 && mealType !== 'Add On') {
                        const price = order.price || MEAL_PRICES[mealType] || 0;
                        const activeProtein = order.proteinOptions?.find(opt => opt.isActive);
                        const premiumProteinPrice = activeProtein?.price || 0;
                        const proteinName = activeProtein?.name || activeProtein?.variantName || '';

                        // Track ordered meals
                        weeklyMealsByType[mealType].ordered += quantity;
                        ordersByDateAndType[date][mealType].ordered += quantity;

                        // Track prices (for non-lunch/dinner meal types)
                        for (let i = 0; i < quantity; i++) {
                            weeklyMealsByType[mealType].price.push(price);
                            ordersByDateAndType[date][mealType].price.push(price);
                        }

                        // Track ALL premium proteins by name (we'll calculate charges later based on subscription count)
                        let premiumProteinInfo = null;
                        if (premiumProteinPrice > 0 && proteinName) {
                            if (!weeklyMealsByType[mealType].premium[proteinName]) {
                                weeklyMealsByType[mealType].premium[proteinName] = 0;
                            }
                            weeklyMealsByType[mealType].premium[proteinName] += quantity;

                            if (!ordersByDateAndType[date][mealType].premium[proteinName]) {
                                ordersByDateAndType[date][mealType].premium[proteinName] = 0;
                            }
                            ordersByDateAndType[date][mealType].premium[proteinName] += quantity;

                            // Store the potential premium protein charge (will be adjusted later based on subscription coverage)
                            if (!ordersByDateAndType[date][mealType].premiumCharge[proteinName]) {
                                ordersByDateAndType[date][mealType].premiumCharge[proteinName] = 0;
                            }
                            ordersByDateAndType[date][mealType].premiumCharge[proteinName] += premiumProteinPrice * quantity;

                            // Temporarily track full charge - will be adjusted in PASS 3
                            premiumProteinInfo = {
                                name: proteinName,
                                charge: premiumProteinPrice * quantity
                            };
                        }

                        // Store order details (will be updated in PASS 3 after pool allocation)
                        orderDetailsByDate[date][mealType].push({
                            recordId: order.recordId,
                            itemName: order.itemName,
                            quantity: quantity,
                            price: price, // Will be updated to 0 if covered
                            isIncluded: false, // Will be updated in PASS 3
                            premiumProtein: premiumProteinInfo
                        });
                    }
                });
            });

            // ===== PASS 2: Allocate subscription pool to dates (prioritize default delivery dates) =====
            // Sort dates: default delivery days first, then others
            const sortedDates = [...datesInWeek].sort((a, b) => {
                const aHasSubscription = customerDetails.subscriptions.some(sub => sub.nextDeliveryDate === a);
                const bHasSubscription = customerDetails.subscriptions.some(sub => sub.nextDeliveryDate === b);
                if (aHasSubscription && !bHasSubscription) return -1;
                if (!aHasSubscription && bHasSubscription) return 1;
                return 0;
            });

            // Find default delivery date for refunds
            const defaultDate = sortedDates.find(date => {
                const matchingSubscriptions = customerDetails.subscriptions.filter(sub =>
                    sub.nextDeliveryDate === date
                );
                return matchingSubscriptions.length > 0;
            }) || datesInWeek[0];

            // Track remaining pool as we allocate
            const remainingPoolByType = {};
            MEAL_TYPES.forEach(mealType => {
                if (mealType !== 'Add On') {
                    const mealData = weeklyMealsByType[mealType];
                    remainingPoolByType[mealType] = mealData.subscribed || 0;
                }
            });

            // Track how many meals from each date are covered by pool
            const coveredByPool = {}; // { date: { mealType: number } }
            datesInWeek.forEach(date => {
                coveredByPool[date] = {};
                MEAL_TYPES.forEach(mealType => {
                    if (mealType !== 'Add On') {
                        coveredByPool[date][mealType] = 0;
                    }
                });
            });

            // Allocate pool to dates (prioritize default delivery dates)
            sortedDates.forEach(date => {
                MEAL_TYPES.forEach(mealType => {
                    if (mealType === 'Add On') return;

                    const dateOrders = ordersByDateAndType[date][mealType];
                    const dateOrdered = dateOrders.ordered || 0;
                    const poolRemaining = remainingPoolByType[mealType] || 0;

                    if (dateOrdered > 0 && poolRemaining > 0) {
                        const covered = Math.min(dateOrdered, poolRemaining);
                        coveredByPool[date][mealType] = covered;
                        remainingPoolByType[mealType] = poolRemaining - covered;
                    }
                });
            });

            // ===== PASS 3: Calculate charges per date following backend logic =====
            datesInWeek.forEach(date => {
                const lockDate = getCutoffTime(date);
                const isLocked = lockDate && new Date() > lockDate;
                const localSkipStatus = deliveryStatusState[date]?.isSkipped;
                const matchingSubscriptions = customerDetails.subscriptions.filter(sub =>
                    sub.nextDeliveryDate === date
                );
                const isDateSkipped = localSkipStatus !== undefined
                    ? localSkipStatus
                    : matchingSubscriptions.some(sub => sub.isSkipped);

                // Collect orders for this date
                const ordersForDate = ordersByDeliveryDate[date] || [];
                const dateOrderedByType = {};
                const dateSubscribedByType = {};

                ordersForDate.forEach(order => {
                    const mealType = order.meal || 'Unknown';
                    if (mealType !== 'Add On') {
                        dateOrderedByType[mealType] = (dateOrderedByType[mealType] || 0) + (order.quantity || 0);
                    }
                });

                matchingSubscriptions.forEach(sub => {
                    const mealType = sub.mealType;
                    dateSubscribedByType[mealType] = (dateSubscribedByType[mealType] || 0) + (sub.mealsIncluded || 0);
                });

                // Build meals structure for this date
                const dateMeals = {};
                let dateCharges = 0;
                let dateRefunds = 0;

                // Process each meal type following backend logic
                MEAL_TYPES.forEach(mealType => {
                    if (mealType === 'Add On') {
                        // Add-ons: apply bulk pricing based on weekly totals per category
                        const addOnOrderDetails = orderDetailsByDate[date]['Add On'] || [];
                        let addOnOrdered = 0;
                        let addOnAmount = 0;

                        // Compute weekly totals per bulk category (across all dates in this week)
                        const weeklyBulkTotals = {};
                        const weeklyAddOns = weeklyMealsByType['Add On'] || {};
                        for (const [dId, qty] of Object.entries(weeklyAddOns)) {
                            // Find dish name from any order detail across all dates
                            let dishName = '';
                            for (const d of datesInWeek) {
                                const detail = (orderDetailsByDate[d]?.['Add On'] || []).find(od => String(od.dishId) === String(dId));
                                if (detail) { dishName = detail.itemName; break; }
                            }
                            const cat = getBulkCategory(dishName);
                            if (cat) {
                                weeklyBulkTotals[cat] = (weeklyBulkTotals[cat] || 0) + qty;
                            }
                        }

                        addOnOrderDetails.forEach(orderDetail => {
                            addOnOrdered += orderDetail.quantity;
                            const cat = getBulkCategory(orderDetail.itemName);
                            let unitPrice = orderDetail.price;

                            if (cat && BULK_PRICING[cat] && weeklyBulkTotals[cat] >= BULK_PRICING[cat].bulkMin) {
                                unitPrice = BULK_PRICING[cat].bulkEach;
                            }

                            const itemTotal = unitPrice * orderDetail.quantity;
                            addOnAmount += itemTotal;
                            // Update the order detail price for display
                            orderDetail.price = unitPrice;
                        });

                        if (addOnOrdered > 0) {
                            dateMeals['Add On'] = {
                                subscribed: 0,
                                ordered: addOnOrdered,
                                delta: addOnOrdered,
                                amount: addOnAmount,
                                premiumProteins: { count: 0, charge: 0 },
                                orderDetails: addOnOrderDetails
                            };
                            dateCharges += addOnAmount;
                        }
                        return;
                    }

                    const dateOrders = ordersByDateAndType[date][mealType];
                    const ordered = dateOrders.ordered || 0;
                    const dateSubscribed = dateSubscribedByType[mealType] || 0;
                    const mealPrice = MEAL_PRICES[mealType] || 0;

                    // Calculate how many are covered by pool for this date
                    const covered = coveredByPool[date][mealType] || 0;
                    const extraOrdered = Math.max(0, ordered - covered);

                    // Calculate delta: ordered - subscribed (for display)
                    let delta = ordered - dateSubscribed;

                    // Special snack logic (using weekly totals for snack calculation, matching billing script)
                    if (mealType === 'Snack') {
                        const weeklySubscribed = weeklyMealsByType[mealType].subscribed || 0;
                        const weeklyOrdered = weeklyMealsByType[mealType].ordered || 0;
                        if (weeklySubscribed === 0 || weeklyOrdered <= weeklySubscribed / snacksPerDay) {
                            delta = ordered - weeklySubscribed / snacksPerDay;
                        } else if (weeklyOrdered > weeklySubscribed) {
                            delta = ordered - weeklySubscribed;
                        } else {
                            delta = 0;
                        }
                    }

                    // Premium proteins: calculate charge based on subscription coverage
                    // Logic: If customer has 3 premium subscriptions but orders 4 premium meats,
                    // only charge for the 4th premium meat
                    let premiumProteinCharge = 0;
                    const premiumProteins = { count: 0, charge: 0 };
                    if (dateOrders.premium && Object.keys(dateOrders.premium).length > 0) {
                        // Get total premium meats ordered for this date
                        const totalPremiumMealsOrdered = Object.values(dateOrders.premium).reduce((sum, qty) => sum + qty, 0);

                        // Get premium subscription count for this meal type (from weekly pool)
                        const weeklyPremiumSubscriptions = weeklyMealsByType[mealType].premiumSubscriptions || 0;

                        // Calculate how many premium meals should be covered by subscription
                        // Premium subscriptions work similarly to meal pool - shared across the week
                        // We need to track how many premium meals from this date should be covered
                        const totalWeeklyPremiumOrdered = weeklyMealsByType[mealType].premium
                            ? Object.values(weeklyMealsByType[mealType].premium).reduce((sum, qty) => sum + qty, 0)
                            : 0;

                        // Calculate premium meals to charge for this date
                        // If weekly premium ordered <= weekly premium subscriptions, no charge
                        // Otherwise, distribute the excess charge proportionally
                        let premiumMealsToCharge = 0;
                        if (totalWeeklyPremiumOrdered > weeklyPremiumSubscriptions) {
                            // There are excess premium meals - calculate this date's share
                            const excessPremiumMeals = totalWeeklyPremiumOrdered - weeklyPremiumSubscriptions;

                            // Distribute excess proportionally to this date
                            const dateRatio = totalPremiumMealsOrdered / totalWeeklyPremiumOrdered;
                            premiumMealsToCharge = Math.ceil(excessPremiumMeals * dateRatio);
                        }

                        // Calculate charge for premium proteins
                        for (const proteinName in dateOrders.premium) {
                            const qty = dateOrders.premium[proteinName];
                            const fullCharge = dateOrders.premiumCharge[proteinName] || 0;

                            // Calculate what portion of this protein should be charged
                            let chargeForThisProtein = 0;
                            if (premiumMealsToCharge > 0) {
                                // Calculate charge proportionally based on excess premium meals
                                chargeForThisProtein = Math.min(fullCharge, fullCharge * (premiumMealsToCharge / totalPremiumMealsOrdered));
                            }

                            premiumProteinCharge += chargeForThisProtein;
                            premiumProteins.count += qty;
                            premiumProteins.charge += chargeForThisProtein;
                        }
                    }

                    // Calculate meal charge based on extra ordered (not covered by pool)
                    let mealCharge = 0;
                    if (extraOrdered > 0) {
                        if (mealType === 'Lunch' || mealType === 'Dinner') {
                            // Use fixed meal price
                            mealCharge = extraOrdered * mealPrice;
                        } else {
                            // Use sorted prices from orders (take first extraOrdered prices)
                            const prices = [...(dateOrders.price || [])].sort((a, b) => a - b);
                            mealCharge = prices.slice(0, extraOrdered).reduce((sum, price) => sum + price, 0);
                        }
                    }

                    const totalCharge = mealCharge + premiumProteinCharge;

                    // Allocate pool coverage to orders (prioritize expensive items)
                    const orderDetails = orderDetailsByDate[date][mealType] || [];
                    // Sort by price descending to prioritize expensive items for subscription coverage
                    const sortedOrderDetails = [...orderDetails].sort((a, b) => {
                        const priceA = mealType === 'Lunch' || mealType === 'Dinner' ? mealPrice : a.price;
                        const priceB = mealType === 'Lunch' || mealType === 'Dinner' ? mealPrice : b.price;
                        return priceB - priceA;
                    });

                    // Calculate premium protein coverage
                    // Premium subscriptions work independently of meal pool
                    const weeklyPremiumSubscriptions = weeklyMealsByType[mealType].premiumSubscriptions || 0;
                    const totalWeeklyPremiumOrdered = weeklyMealsByType[mealType].premium
                        ? Object.values(weeklyMealsByType[mealType].premium).reduce((sum, qty) => sum + qty, 0)
                        : 0;
                    const totalPremiumMealsOrdered = Object.values(dateOrders.premium || {}).reduce((sum, qty) => sum + qty, 0);

                    // Calculate how many premium meals should be charged on this date
                    let premiumMealsToCharge = 0;
                    if (totalWeeklyPremiumOrdered > weeklyPremiumSubscriptions) {
                        const excessPremiumMeals = totalWeeklyPremiumOrdered - weeklyPremiumSubscriptions;
                        const dateRatio = totalPremiumMealsOrdered / totalWeeklyPremiumOrdered;
                        premiumMealsToCharge = Math.ceil(excessPremiumMeals * dateRatio);
                    }

                    let remainingCovered = covered;
                    let remainingPremiumToCharge = premiumMealsToCharge;
                    const finalOrderDetails = [];

                    for (const orderDetail of sortedOrderDetails) {
                        // Calculate premium charge for this order
                        let premiumChargeForOrder = null;
                        if (orderDetail.premiumProtein) {
                            if (remainingPremiumToCharge > 0) {
                                // This premium meal should be charged
                                const mealsToCharge = Math.min(orderDetail.quantity, remainingPremiumToCharge);
                                const chargePerMeal = orderDetail.premiumProtein.charge / orderDetail.quantity;
                                premiumChargeForOrder = {
                                    ...orderDetail.premiumProtein,
                                    charge: chargePerMeal * mealsToCharge
                                };
                                remainingPremiumToCharge -= mealsToCharge;
                            } else {
                                // Premium is covered by subscription
                                premiumChargeForOrder = {
                                    ...orderDetail.premiumProtein,
                                    charge: 0
                                };
                            }
                        }

                        if (remainingCovered >= orderDetail.quantity) {
                            // All meals in this order are covered by meal pool
                            finalOrderDetails.push({
                                ...orderDetail,
                                isIncluded: true,
                                price: 0, // Covered by subscription
                                premiumProtein: premiumChargeForOrder
                            });
                            remainingCovered -= orderDetail.quantity;
                        } else if (remainingCovered > 0) {
                            // Partially covered - split into included and extra
                            const includedQty = remainingCovered;
                            const extraQty = orderDetail.quantity - remainingCovered;

                            // Create two entries: one included, one extra
                            finalOrderDetails.push({
                                ...orderDetail,
                                quantity: includedQty,
                                isIncluded: true,
                                price: 0,
                                premiumProtein: premiumChargeForOrder ? {
                                    ...premiumChargeForOrder,
                                    // Split premium charge proportionally
                                    charge: premiumChargeForOrder.charge * (includedQty / orderDetail.quantity)
                                } : null
                            });

                            finalOrderDetails.push({
                                ...orderDetail,
                                quantity: extraQty,
                                isIncluded: false,
                                price: mealType === 'Lunch' || mealType === 'Dinner' ? mealPrice : orderDetail.price,
                                premiumProtein: premiumChargeForOrder ? {
                                    ...premiumChargeForOrder,
                                    // Split premium charge proportionally
                                    charge: premiumChargeForOrder.charge * (extraQty / orderDetail.quantity)
                                } : null
                            });
                            remainingCovered = 0;
                        } else {
                            // No coverage left
                            finalOrderDetails.push({
                                ...orderDetail,
                                isIncluded: false,
                                price: mealType === 'Lunch' || mealType === 'Dinner' ? mealPrice : orderDetail.price,
                                premiumProtein: premiumChargeForOrder
                            });
                        }
                    }

                    dateMeals[mealType] = {
                        subscribed: dateSubscribed,
                        ordered,
                        delta: delta,
                        amount: totalCharge,
                        premiumProteins: premiumProteins,
                        orderDetails: finalOrderDetails // Include order details for display
                    };

                    if (totalCharge > 0) {
                        dateCharges += totalCharge;
                    }
                });

                // Apply refunds to default delivery date only (for unused pool)
                const isDefaultDate = date === defaultDate;
                if (isDefaultDate) {
                    MEAL_TYPES.forEach(mealType => {
                        if (mealType === 'Add On') return;

                        const mealPrice = MEAL_PRICES[mealType] || 0;
                        let unusedPool = remainingPoolByType[mealType] || 0;

                        // Snack refund: only refund based on effective pool (subscribed/snacksPerDay), matching billing script
                        if (mealType === 'Snack') {
                            const weeklySubscribed = weeklyMealsByType[mealType].subscribed || 0;
                            const weeklyOrdered = weeklyMealsByType[mealType].ordered || 0;
                            const effectivePool = weeklySubscribed / snacksPerDay;
                            unusedPool = Math.max(0, effectivePool - weeklyOrdered);
                        }

                        if (unusedPool > 0) {
                            const isPremiumPlan = mealType !== 'Snack' && (weeklyMealsByType[mealType]?.premiumSubscriptions || 0) > 0;
                            const perMealRefund = mealPrice + (isPremiumPlan ? PREMIUM_SURCHARGE : 0);
                            const refundAmount = unusedPool * perMealRefund * SUBSCRIPTION_DISCOUNT;
                            dateRefunds += refundAmount;

                            // Update meal data to reflect refund
                            if (!dateMeals[mealType]) {
                                dateMeals[mealType] = {
                                    subscribed: 0,
                                    ordered: 0,
                                    delta: -unusedPool,
                                    amount: -refundAmount,
                                    premiumProteins: { count: 0, charge: 0 }
                                };
                            } else {
                                dateMeals[mealType].delta -= unusedPool;
                                dateMeals[mealType].amount -= refundAmount;
                            }
                        }
                    });
                }

                const totalDateSubscribed = Object.values(dateMeals).reduce((sum, m) => sum + (m.subscribed || 0), 0);
                const totalDateOrdered = Object.values(dateMeals).reduce((sum, m) => sum + (m.ordered || 0), 0);

                dates[date] = {
                    weekKey,
                    isLocked,
                    isSkipped: isDateSkipped,
                    totalSubscribed: totalDateSubscribed,
                    totalOrdered: totalDateOrdered,
                    hasChanges: dateCharges > 0 || dateRefunds > 0,
                    charges: dateCharges,
                    refunds: dateRefunds,
                    net: dateCharges - dateRefunds,
                    meals: dateMeals
                };

                if (dateCharges > 0 || dateRefunds > 0) {
                    totalCharges += dateCharges;
                    totalRefunds += dateRefunds;
                    if (!datesWithChanges.includes(date)) {
                        datesWithChanges.push(date);
                    }
                }
            });

            // ===== Delivery Fee Calculation (per week) =====
            // deliveryFees is raw map from Airtable: "zipCode | day | time" → cost
            const deliveryFees = customerDetails.deliveryFees || {}; // keys already lowercase from backend

            if (Object.keys(deliveryFees).length > 0) {
                // Build subscription lookup by day name
                const subByDay = {};
                (customerDetails.subscriptions || []).forEach(sub => {
                    if (sub.deliveryDay) {
                        subByDay[sub.deliveryDay.toLowerCase()] = sub;
                    }
                });

                // Per-date delta = orderedFee(at this date's effective slot) - paidFee(from subscription on that day-of-week)
                const subDaysCovered = new Set();

                datesInWeek.forEach(date => {
                    const ordersForDate = ordersByDeliveryDate[date] || [];
                    const hasOrders = ordersForDate.some(o => o.quantity > 0);
                    if (!hasOrders) return;

                    const dayName = getDayOfWeek(date);
                    const dayLower = dayName.toLowerCase();
                    const zipCode = ordersForDate[0]?.zipCode || '';
                    const matchingSub = subByDay[dayLower];
                    const existingOrderTime = ordersForDate.find(o => o.deliveryTime)?.deliveryTime;
                    const defaultTime = ({ sunday: 'Evening', monday: 'Evening', tuesday: 'Evening', wednesday: 'Midday', thursday: 'Midday' })[dayLower] || 'Evening';
                    const orderedTime = deliveryTimeOverrides[date] || existingOrderTime || matchingSub?.deliveryTime || defaultTime;
                    const orderedKey = `${zipCode} | ${dayName} | ${orderedTime}`.toLowerCase();
                    const orderedFee = deliveryFees[orderedKey] ?? 9.99;

                    let paidFee = 0;
                    if (matchingSub && !matchingSub.isSkipped) {
                        const paidKey = `${matchingSub.zipCode || ''} | ${matchingSub.deliveryDay} | ${matchingSub.deliveryTime || ''}`.toLowerCase();
                        paidFee = deliveryFees[paidKey] || 0;
                        subDaysCovered.add(dayLower);
                    }

                    const delta = orderedFee - paidFee;
                    if (delta !== 0 && dates[date]) {
                        dates[date].deliveryFee = (dates[date].deliveryFee || 0) + delta;
                        dates[date].hasChanges = true;
                        totalCharges += delta;
                        totalDeliveryFee += delta;
                        if (!datesWithChanges.includes(date)) datesWithChanges.push(date);
                    }
                });

                // Subscription days in this week with no orders → refund the paid fee once per unique slot
                const refundedKeys = new Set();
                (customerDetails.subscriptions || []).forEach(sub => {
                    if (!sub.deliveryDay || sub.isSkipped) return;
                    const dayLower = sub.deliveryDay.toLowerCase();
                    if (subDaysCovered.has(dayLower)) return;

                    const paidKey = `${sub.zipCode || ''} | ${sub.deliveryDay} | ${sub.deliveryTime || ''}`.toLowerCase();
                    if (refundedKeys.has(paidKey)) return;
                    refundedKeys.add(paidKey);

                    const matchingDate = datesInWeek.find(date => {
                        const dayName = getDayOfWeek(date).toLowerCase();
                        return dayName === dayLower;
                    });
                    if (!matchingDate || !dates[matchingDate]) return;

                    const paidFee = deliveryFees[paidKey] || 0;
                    if (paidFee === 0) return;

                    dates[matchingDate].deliveryFee = (dates[matchingDate].deliveryFee || 0) - paidFee;
                    dates[matchingDate].hasChanges = true;
                    totalCharges -= paidFee;
                    totalDeliveryFee -= paidFee;
                    if (!datesWithChanges.includes(matchingDate)) datesWithChanges.push(matchingDate);
                });
            }

            // After processing all dates in the week, save premium usage data
            premiumUsageByWeek[weekKey] = {};
            //debug
            //console.log('weeklyMealsByType', weeklyMealsByType);
            MEAL_TYPES.forEach(mealType => {
                if (mealType !== 'Add On') {
                    const weeklyPremiumSubscriptions = weeklyMealsByType[mealType].premiumSubscriptions || 0;
                    const totalWeeklyPremiumOrdered = weeklyMealsByType[mealType].premium
                        ? Object.values(weeklyMealsByType[mealType].premium).reduce((sum, qty) => sum + qty, 0)
                        : 0;

                    const hasExceeded = totalWeeklyPremiumOrdered >= weeklyPremiumSubscriptions;

                    premiumUsageByWeek[weekKey][mealType] = {
                        subscribed: weeklyPremiumSubscriptions,
                        ordered: totalWeeklyPremiumOrdered,
                        // Use >= instead of > because when all slots are used (ordered >= subscribed),
                        // the NEXT premium selection should be charged
                        hasExceeded: hasExceeded
                    };

                }
            });
        });

        return {
            summary: {
                totalCharges,
                totalRefunds,
                totalDeliveryFee,
                net: totalCharges - totalRefunds,
                netChange: totalCharges - totalRefunds, // Alias for compatibility
                netCharge: totalCharges - totalRefunds, // Alias for BillingChangeBanner
                datesWithChanges,
                customerName: customerDetails?.customer?.name
            },
            dates,
            premiumUsageByWeek // Track premium subscriptions vs orders per week per meal type
        };
    }, [customerDetails, deliveryDates, ordersByDeliveryDate, deliveryStatusState, deliveryTimeOverrides]);

    // API functions
    const fetchMealPlan = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const [{ customerDetails, MealsByDate }, paymentHistoryResult] = await Promise.all([
                mealPlanService.fetchMealPlan(token),
                mealPlanService.getPaymentHistory(token).catch(() => ({ paymentsByDate: {} }))
            ]);
            const sortedDates = sortDeliveryDates(Object.keys(MealsByDate));
            const landingDate = sortedDates.find(date =>
                MealsByDate[date].reduce((sum, o) => sum + (o.quantity || 0), 0) > 0
            ) || sortedDates[0];

            setCustomerDetails(customerDetails);
            setDeliveryDates(sortedDates);
            setOrdersByDeliveryDate(MealsByDate);
            setSelectedDeliveryDate(landingDate);
            setOrders(MealsByDate[landingDate] || []);
            setPriorPaymentsByDate(paymentHistoryResult.paymentsByDate || {});
            setPaymentRecords(paymentHistoryResult.paymentRecords || {});
            // console.log('customerDetails', customerDetails);
            // console.log('MealsByDate', MealsByDate);
            // console.log('sortedDates', sortedDates);

        } catch (err) {
            const errorMsg = err.response?.data?.error || 'Failed to load meal plan. Please try again later.';
            setError(errorMsg);
            setTimeout(() => setError(null), 5000);
        } finally {
            setLoading(false);
        }
    }, [token]);

    // (Stripe cancel redirect is handled in the initial load useEffect above)

    // Detect price changes whenever orders or delivery status changes
    useEffect(() => {
        if (customerDetails && deliveryDates.length > 0) {
            const changes = calculateBillingChanges();
            //debug
            //console.log('billingChanges', changes);
            setBillingChanges(changes);
        }
    }, [ordersByDeliveryDate, customerDetails, deliveryDates, deliveryStatusState, calculateBillingChanges]);

    // Helper functions
    const getSubscriptionCount = (mealType = null) => {
        if (!customerDetails?.subscriptions) return 0;
        const subscription = customerDetails.subscriptions.find(sub =>
            (mealType ? sub.meal?.toLowerCase().includes(mealType.toLowerCase()) : true) &&
            sub.deliveryDay.toLowerCase() === getDayOfWeek(selectedDeliveryDate).toLowerCase()
        );
        return subscription?.mealsIncluded || 0;
    };

    // Update delivery status for all dates based on subscription data
    const updateDeliveryStatuses = () => {
        if (!customerDetails?.subscriptions || !deliveryDates || deliveryDates.length === 0) return;

        const newStatuses = {};

        deliveryDates.forEach(date => {
            // Parse date to get day of week
            const parsedDate = parseEstDate(date);
            const dayOfWeek = parsedDate.format("dddd");

            // Find subscriptions for this day of week
            const daySubscription = customerDetails.subscriptions.find(sub =>
                sub.deliveryDay?.toLowerCase() === dayOfWeek.toLowerCase()
            );
            if (!daySubscription) {
                newStatuses[date] = { isSkipped: false, hasSubscription: false };
            } else {
                newStatuses[date] = {
                    isSkipped: daySubscription.isSkipped,
                    hasSubscription: true,
                    nextDeliveryDate: daySubscription.nextDeliveryDate
                };
            }
        });

        setDeliveryStatusState(newStatuses);
    };

    const getOrderedMealCount = (mealType, deliveryDate = null) => {
        const targetDate = deliveryDate || selectedDeliveryDate;
        const orderedCount = orders
            .filter(order =>
                order.meal === mealType &&
                order.deliveryDate === targetDate &&
                order.quantity > 0
            )
            .reduce((total, order) => total + order.quantity, 0);
        return orderedCount;
    };

    const getPriceText = (mealType, subscribed, ordered) => {
        const snacksPerDay = customerDetails.customer.snacksPerDay;
        let finalText = [];
        if (mealType === 'Snack') {
            finalText.push(snacksPerDay > 1 ? `Recommended: ${snacksPerDay} snacks per day` : 'Recommended: 1 snack per day');
        }
        if (MEAL_PRICES[mealType] && mealType !== 'Add On' && mealType !== 'Snack' && mealType !== 'Breakfast') {
            finalText.push(`$${MEAL_PRICES[mealType]} per additional meal`);
        }
        return finalText.length > 0 ? `(${finalText.join(', ')})` : '';
    };

    const updateOrdersState = (updatedOrders) => {
        const sorted = [
            ...updatedOrders.filter(order => order.quantity > 0),
            ...updatedOrders.filter(order => order.quantity === 0)
        ];
        setOrders(sorted);
        setOrdersByDeliveryDate(prev => ({
            ...prev,
            [selectedDeliveryDate]: sorted
        }));

        // Update modal index if modal is open
        if (selectedMeal && showModal) {
            const newIndex = sorted.findIndex(order =>
                order.recordId === selectedMeal.recordId &&
                order.dishId === selectedMeal.dishId &&
                order.meal === selectedMeal.meal
            );
            if (newIndex !== -1) {
                setSelectedMeal({ ...sorted[newIndex], actualIndex: newIndex });
            }
        }
    };

    // Helper function to optimize pending changes by filtering redundant operations
    // Groups changes by recordId and applies filtering logic similar to backend
    const optimizePendingChanges = useCallback((changes) => {
        if (!changes || changes.length === 0) return [];

        // Sort by timestamp
        const sortedChanges = [...changes].sort((a, b) => a.timestamp - b.timestamp);

        // Group changes by recordId. Date-scoped changes (no recordId — e.g. CHANGE_DELIVERY_TIME)
        // pass through unmodified so they're never accidentally dropped.
        const changesByRecordId = {};
        const dateScopedChanges = [];
        for (const change of sortedChanges) {
            const recordId = change.recordId || change.order?.recordId;
            if (!recordId) {
                dateScopedChanges.push(change);
                continue;
            }

            if (!changesByRecordId[recordId]) {
                changesByRecordId[recordId] = [];
            }
            changesByRecordId[recordId].push({ ...change, _recordId: recordId });
        }

        const filteredChanges = [];
        for (const recordId in changesByRecordId) {
            const groupChanges = changesByRecordId[recordId].sort((a, b) => a.timestamp - b.timestamp);

            // Find all REMOVE_DISH changes where recordId starts with temp-
            const removeIndices = [];
            groupChanges.forEach((change, index) => {
                if (change.type === 'REMOVE_DISH' && recordId.startsWith('temp-')) {
                    removeIndices.push(index);
                }
            });

            if (removeIndices.length > 0) {

                // ADD then REMOVE for temp- recordId cancels out
                // Keep only changes that come after the last REMOVE_DISH
                const lastRemoveIndex = removeIndices[removeIndices.length - 1];
                const changesAfterLastRemove = groupChanges.slice(lastRemoveIndex + 1);
                if (changesAfterLastRemove.length > 0) {
                    filteredChanges.push(...changesAfterLastRemove);
                }


            } else {
                // Check if last change is REMOVE_DISH with non-temp recordId
                const lastChange = groupChanges[groupChanges.length - 1];
                if (lastChange.type === 'REMOVE_DISH' && !recordId.startsWith('temp-')) {
                    // Keep only the last REMOVE_DISH change (discard all modifications before deletion)
                    filteredChanges.push(lastChange);
                } else {
                    // No special REMOVE_DISH conditions, include all changes
                    filteredChanges.push(...groupChanges);
                }
            }
        }

        // Sort filtered changes by timestamp again to maintain overall order
        return [...filteredChanges, ...dateScopedChanges].sort((a, b) => a.timestamp - b.timestamp);
    }, []);

    // Helper function to track pending changes for a delivery date
    // Optimizes changes by filtering redundant operations (e.g., ADD then REMOVE cancels out)
    const trackPendingChange = useCallback((deliveryDate, change) => {
        // Add unique changeId to track individual actions
        const changeWithId = {
            ...change,
            changeId: `change-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        };

        setPendingChanges(prev => {
            const dateChanges = prev[deliveryDate] || {
                isDirty: false,
                changes: [],
                monetaryChange: false
            };

            // Add the new change and optimize
            const allChanges = [...dateChanges.changes, changeWithId];
            const optimizedChanges = optimizePendingChanges(allChanges);

            return {
                ...prev,
                [deliveryDate]: {
                    isDirty: optimizedChanges.length > 0,
                    changes: optimizedChanges,
                    monetaryChange: optimizedChanges.some(c => c.monetaryChange)
                }
            };
        });
    }, [optimizePendingChanges]);

    // Helper function to clear pending changes for a delivery date
    const clearPendingChanges = useCallback((deliveryDate) => {
        setPendingChanges(prev => {
            const { [deliveryDate]: _, ...rest } = prev;
            return rest;
        });
    }, []);

    // Helper function to remove only successful changes from pending changes
    const removeSuccessfulChanges = useCallback((deliveryDate, successfulChangeIds) => {
        setPendingChanges(prev => {
            const dateChanges = prev[deliveryDate];
            if (!dateChanges) return prev;

            const remainingChanges = dateChanges.changes.filter(
                change => !successfulChangeIds.includes(change.changeId)
            );

            if (remainingChanges.length === 0) {
                // All changes succeeded, remove the date entirely
                const { [deliveryDate]: _, ...rest } = prev;
                return rest;
            }

            return {
                ...prev,
                [deliveryDate]: {
                    isDirty: remainingChanges.length > 0,
                    changes: remainingChanges,
                    monetaryChange: remainingChanges.some(c => c.monetaryChange)
                }
            };
        });
    }, []);

    // Handle delivery time change for a date — updates override and tracks pending change.
    // If the new time matches the persisted time (existing order or subscription), clears the override
    // and removes any pending CHANGE_DELIVERY_TIME so we don't show "unsaved" for a no-op revert.
    const handleDeliveryTimeChange = useCallback((deliveryDate, newTime) => {
        const ordersForDate = ordersByDeliveryDate[deliveryDate] || [];
        const dayName = new Date(deliveryDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' });
        const matchingSub = (customerDetails?.subscriptions || []).find(s =>
            (s.deliveryDay || '').toLowerCase() === dayName.toLowerCase() && !s.isSkipped
        );
        const existingOrderTime = ordersForDate.find(o => o.deliveryTime)?.deliveryTime;
        const persistedTime = existingOrderTime || matchingSub?.deliveryTime || '';
        const isRevert = persistedTime && persistedTime.toLowerCase() === newTime.toLowerCase();

        setDeliveryTimeOverrides(prev => {
            if (isRevert) {
                const { [deliveryDate]: _, ...rest } = prev;
                return rest;
            }
            return { ...prev, [deliveryDate]: newTime };
        });

        setPendingChanges(prev => {
            const dateChanges = prev[deliveryDate] || { isDirty: false, changes: [], monetaryChange: false };
            const filtered = dateChanges.changes.filter(c => c.type !== 'CHANGE_DELIVERY_TIME');

            const updatedChanges = isRevert ? filtered : [
                ...filtered,
                {
                    type: 'CHANGE_DELIVERY_TIME',
                    deliveryTime: newTime,
                    deliveryDate,
                    monetaryChange: true,
                    changeId: `change-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    timestamp: Date.now()
                }
            ];

            if (updatedChanges.length === 0) {
                const { [deliveryDate]: _, ...rest } = prev;
                return rest;
            }

            return {
                ...prev,
                [deliveryDate]: {
                    isDirty: updatedChanges.length > 0,
                    changes: updatedChanges,
                    monetaryChange: updatedChanges.some(c => c.monetaryChange)
                }
            };
        });
    }, [ordersByDeliveryDate, customerDetails]);

    // Helper function to update orders with real Airtable recordIds after save
    const updateOrderRecordIds = useCallback((deliveryDate, results) => {
        if (!results || !Array.isArray(results)) return;

        // If a CHANGE_DELIVERY_TIME succeeded, propagate the new time onto the local orders
        const successfulTimeChange = results.find(r => r.type === 'CHANGE_DELIVERY_TIME' && r.success);
        if (successfulTimeChange && successfulTimeChange.deliveryTime) {
            const newTime = successfulTimeChange.deliveryTime;
            setOrdersByDeliveryDate(prev => {
                const ordersForDate = prev[deliveryDate] || [];
                return { ...prev, [deliveryDate]: ordersForDate.map(o => ({ ...o, deliveryTime: newTime })) };
            });
            setDeliveryTimeOverrides(prev => {
                const { [deliveryDate]: _, ...rest } = prev;
                return rest;
            });
        }

        // Find ADD_DISH results that have new recordIds
        const recordIdMappings = results
            .filter(r => r.type === 'ADD_DISH' && r.success && r.recordId && r.originalTempRecordId)
            .map(r => ({ oldId: r.originalTempRecordId, newId: r.recordId }));

        if (recordIdMappings.length === 0) return;

        //console.log('📝 Updating order recordIds:', recordIdMappings);

        // Update orders for the delivery date
        setOrdersByDeliveryDate(prev => {
            const ordersForDate = prev[deliveryDate] || [];
            const updatedOrders = ordersForDate.map(order => {
                const mapping = recordIdMappings.find(m => m.oldId === order.recordId);
                if (mapping) {
                    return { ...order, recordId: mapping.newId };
                }
                return order;
            });
            return { ...prev, [deliveryDate]: updatedOrders };
        });

        // Also update current orders if viewing the same date
        if (selectedDeliveryDate === deliveryDate) {
            setOrders(prev => prev.map(order => {
                const mapping = recordIdMappings.find(m => m.oldId === order.recordId);
                if (mapping) {
                    return { ...order, recordId: mapping.newId };
                }
                return order;
            }));
        }
    }, [selectedDeliveryDate]);

    // Helper function to mark original ingredients as active in ingredient options
    // Also filters out allergy ingredients from being active
    // Uses allergyIngredientIds from customerDetails (fetched from backend API)
    const markOriginalIngredientsActive = (order) => {
        const mealType = order.meal || 'Unknown';
        const deliveryDate = order.deliveryDate || 'Unknown';
        // Get original ingredient IDs
        const originalIngredientIds = order.originalIngredients?.map(ing => ing.id) || [];

        // Get allergy ingredient IDs from customerDetails (populated by backend API)
        const allergyIngredientIds = customerDetails?.customer?.clientAllergies[order.meal] || [];

        // Check if customer is premium and get dish name for premium protein lookup
        const isPremiumCustomer = subscriptions?.some(subscription => subscription?.meal?.toLowerCase().includes(mealType?.toLowerCase()) && subscription?.nextDeliveryDate?.toLowerCase() === deliveryDate?.toLowerCase() && (subscription?.tags?.includes('Premium Meat Only') || subscription?.tags?.includes('Premium Meat Only Paid')));

        const proteinOptions = order.proteinOptions;

        // Filter out allergy ingredients from the original ingredients
        let filteredIngredientIds = originalIngredientIds.filter(id => !allergyIngredientIds.includes(id));

        // Mark options as active if they're in the filtered original ingredients
        const markOptions = (options) => {
            if (!options) return [];
            return options.map(opt => ({
                ...opt,
                isActive: filteredIngredientIds.includes(opt.id)
            }));
        };

        // if premium customer, randomly pick a premium protein which is not an allergy
        if (isPremiumCustomer) {
            const premiumProtein = proteinOptions.filter(opt => opt.variantType?.toLowerCase() === 'premium' && !allergyIngredientIds.includes(opt.id));
            if (premiumProtein.length > 0) {
                const randomIndex = Math.floor(Math.random() * premiumProtein.length);
                const selectedPremiumId = premiumProtein[randomIndex].id;
                proteinOptions[randomIndex].isActive = true;
                filteredIngredientIds.push(selectedPremiumId);

                //mark other protein options as inactive and remove from filteredIngredientIds
                proteinOptions.forEach(opt => {
                    if (opt.id !== selectedPremiumId) {
                        opt.isActive = false;
                        filteredIngredientIds = filteredIngredientIds.filter(id => id !== opt.id);
                    }
                });
            }
        }

        // Collect active ingredients from all five option arrays
        const allOptions = [
            ...(order.proteinOptions || []),
            ...(order.veggieOptions || []),
            ...(order.starchOptions || []),
            ...(order.sauceOptions || []),
            ...(order.garnishOptions || [])
        ];
        const finalIngredients = allOptions.filter(opt => filteredIngredientIds.includes(opt.id));
        return {
            proteinOptions: markOptions(order.proteinOptions),
            veggieOptions: markOptions(order.veggieOptions),
            starchOptions: markOptions(order.starchOptions),
            sauceOptions: markOptions(order.sauceOptions),
            garnishOptions: markOptions(order.garnishOptions),
            finalIngredients
        };
    };

    // Event handlers
    const handleDeliveryDateChange = (deliveryDate) => {
        setSelectedDeliveryDate(deliveryDate);
        const ordersForDate = ordersByDeliveryDate[deliveryDate] || [];
        setOrders(ordersForDate);
    };

    const openModal = (meal, actualIndex) => {
        setSelectedMeal({ ...meal, actualIndex });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedMeal(null);
        setShowAddedBanner(false);
    };

    const handleQuantityChange = (index, newQuantity) => {
        const order = orders[index];
        const oldQuantity = order.quantity;
        const finalQuantity = Math.max(0, parseInt(newQuantity) || 0);
        const scrollTop = modalRef.current?.scrollTop || 0;

        if (finalQuantity === oldQuantity) return; // No change

        let updatedOrders = [...orders];
        // Track the recordId of the order we're modifying/adding for modal lookup
        let targetRecordId = order.recordId;

        if (finalQuantity > oldQuantity) {
            // Adding a meal - local state update only
            if (order.isCore && !order.isOrdered) {
                // First time adding a core meal - mark original ingredients as active (minus allergies)
                const markedIngredients = markOriginalIngredientsActive(order);

                updatedOrders[index] = {
                    ...updatedOrders[index],
                    quantity: 1,
                    isOrdered: true,
                    hasUnsavedChange: true,
                    // Apply marked ingredients (original ingredients minus allergies)
                    proteinOptions: markedIngredients.proteinOptions,
                    veggieOptions: markedIngredients.veggieOptions,
                    starchOptions: markedIngredients.starchOptions,
                    sauceOptions: markedIngredients.sauceOptions,
                    garnishOptions: markedIngredients.garnishOptions,
                    finalIngredients: markedIngredients.finalIngredients
                };
                targetRecordId = updatedOrders[index].recordId;

                // Effective delivery time for this date — mirror the summary modal's chain:
                // dropdown override → sibling order → matching subscription → day-of-week default
                const dayName = new Date(selectedDeliveryDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' });
                const dayLower = dayName.toLowerCase();
                const defaultTimeByDay = ({ sunday: 'Evening', monday: 'Evening', tuesday: 'Evening', wednesday: 'Midday', thursday: 'Midday' })[dayLower] || 'Evening';
                const siblingDeliveryTime = orders.find(o => o.deliveryTime)?.deliveryTime || '';
                const matchingSub = (customerDetails?.subscriptions || []).find(sub =>
                    (sub.deliveryDay || '').toLowerCase() === dayLower && !sub.isSkipped
                );
                const subDeliveryTime = matchingSub?.deliveryTime || '';
                const effectiveDeliveryTime = deliveryTimeOverrides[selectedDeliveryDate]
                    || siblingDeliveryTime
                    || subDeliveryTime
                    || defaultTimeByDay;

                // Track the change
                trackPendingChange(selectedDeliveryDate, {
                    type: 'ADD_DISH',
                    order: { ...updatedOrders[index], deliveryTime: effectiveDeliveryTime, subscriptionDeliveryTime: subDeliveryTime },
                    timestamp: Date.now(),
                    monetaryChange: true
                });
            } else {
                // Adding another serving of an existing ordered dish — deliveryTime already on order via spread
                const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                const newOrder = {
                    ...order,
                    recordId: tempId,
                    quantity: 1,
                    hasUnsavedChange: true
                };
                updatedOrders.push(newOrder);
                targetRecordId = tempId;

                // Track the change
                trackPendingChange(selectedDeliveryDate, {
                    type: 'ADD_DISH',
                    order: newOrder,
                    timestamp: Date.now(),
                    monetaryChange: true
                });
            }

            // Sort: ordered items first, then unordered
            const sorted = [
                ...updatedOrders.filter(o => o.quantity > 0),
                ...updatedOrders.filter(o => o.quantity === 0)
            ];

            // Find the meal in the sorted array for modal
            const reorderedIndex = sorted.findIndex(o =>
                o.recordId === targetRecordId &&
                o.dishId === order.dishId &&
                o.meal === order.meal
            );

            // Update orders state
            flushSync(() => {
                setOrders(sorted);
                setOrdersByDeliveryDate(prev => ({
                    ...prev,
                    [selectedDeliveryDate]: sorted
                }));
            });

            // setSuccessMessage(`Added ${order.itemName}! Remember to "Save Changes" in Order Summary.`);
            // setTimeout(() => setSuccessMessage(''), 5000);

            if (reorderedIndex !== -1) {
                const mealForModal = { ...sorted[reorderedIndex], actualIndex: reorderedIndex };
                setSelectedMeal(mealForModal);
                setShowModal(true);
                setShowAddedBanner(true);
            }

        } else {
            // Removing a meal - local state update only
            const hasSameDish = updatedOrders.some((obj, i) =>
                i !== index &&
                obj.meal === order.meal &&
                obj.dishId === order.dishId &&
                obj.deliveryDate === order.deliveryDate &&
                obj.itemName === order.itemName
            );

            // Track the change before modifying state
            trackPendingChange(selectedDeliveryDate, {
                type: 'REMOVE_DISH',
                recordId: order.recordId,
                dishName: order.itemName,
                timestamp: Date.now(),
                monetaryChange: true
            });

            if (!hasSameDish) {
                // Keep the dish in list but mark as not ordered
                updatedOrders[index] = {
                    ...updatedOrders[index],
                    quantity: 0,
                    isOrdered: false,
                    hasUnsavedChange: false
                };
            } else {
                // Remove the duplicate entry
                updatedOrders = updatedOrders.filter((_, i) => i !== index);
            }

            flushSync(() => updateOrdersState(updatedOrders));

            // setSuccessMessage(`Removed ${order.itemName}. Remember to "Save Changes" in Order Summary.`);
            // setTimeout(() => setSuccessMessage(''), 5000);
        }

        if (modalRef.current) modalRef.current.scrollTop = scrollTop;
    };

    const handleProteinSubstitution = (orderIndex, newProteinId, oldProteinId) => {
        const order = orders[orderIndex];
        const newProteinOption = order.proteinOptions.find(opt => opt.id === newProteinId);
        const upgradePrice = newProteinOption?.price || 0;

        // Only show modal if protein has an upgrade price
        if (upgradePrice > 0) {
            // Check if old protein was premium (if so, we're replacing, not adding)
            const oldProteinOption = order.proteinOptions?.find(opt => opt.id === oldProteinId);
            const oldProteinPrice = oldProteinOption?.price || 0;
            const isReplacingPremium = oldProteinPrice > 0;

            // Use pre-calculated premium usage data from billingChanges
            if (!isReplacingPremium && billingChanges?.premiumUsageByWeek) {
                const mealType = order.meal;

                // Find which week this delivery date belongs to
                const datesByWeek = groupDatesByWeek(deliveryDates);
                let weekKey = null;
                for (const [key, datesInWeek] of Object.entries(datesByWeek)) {
                    if (datesInWeek.includes(selectedDeliveryDate)) {
                        weekKey = key;
                        break;
                    }
                }

                // Check if premium subscription limit has been exceeded
                const premiumUsage = billingChanges.premiumUsageByWeek[weekKey]?.[mealType];
                if (premiumUsage?.hasExceeded) {
                    // Customer has used up their premium subscription plan
                    setShowPriceConfirm({
                        orderIndex,
                        newProteinId,
                        oldProteinId,
                        proteinName: newProteinOption.variantName || newProteinOption.name,
                        price: upgradePrice
                    });
                    return;
                }
            }
        }

        executeProteinSubstitution(orderIndex, newProteinId, oldProteinId, 0);
    };

    const executeProteinSubstitution = (orderIndex, newProteinId, oldProteinId, upgradePrice = 0) => {
        const scrollTop = modalRef.current?.scrollTop || 0;
        const order = orders[orderIndex];
        const newProteinOption = order.proteinOptions.find(opt => opt.id === newProteinId);

        // Update local state - update protein options to reflect new selection
        const updatedProteinOptions = order.proteinOptions.map(opt => ({
            ...opt,
            isActive: opt.id === newProteinId
        }));

        // Update finalIngredients - replace old protein with new (maintain {id, name} structure)
        let updatedFinalIngredients = [...(order.finalIngredients || [])];
        if (oldProteinId) {
            updatedFinalIngredients = updatedFinalIngredients.filter(ing => ing.id !== oldProteinId);
        }
        if (newProteinId) {
            updatedFinalIngredients.push({ id: newProteinId, name: newProteinOption?.name || '' });
        }

        const updatedOrder = {
            ...order,
            proteinOptions: updatedProteinOptions,
            finalIngredients: updatedFinalIngredients,
            hasUnsavedChange: true
        };

        const updatedOrders = [...orders];
        updatedOrders[orderIndex] = updatedOrder;

        // Track the change
        const oldProteinOption = order.proteinOptions?.find(opt => opt.id === oldProteinId);
        trackPendingChange(selectedDeliveryDate, {
            type: 'REPLACE_PROTEIN',
            recordId: order.recordId,
            dishName: order.itemName,
            timestamp: Date.now(),
            newProteinId,
            newProteinName: newProteinOption?.name || '',
            oldProteinId,
            oldProteinName: oldProteinOption?.name || '',
            upgradePrice,
            monetaryChange: upgradePrice > 0 ? true : false
        });

        flushSync(() => updateOrdersState(updatedOrders));

        const message = upgradePrice > 0
            ? `Protein upgraded! $${upgradePrice} will be charged when you save changes.`
            : 'Protein updated! Remember to "Save Changes" in Order Summary.';

        // setSuccessMessage(message);
        // setTimeout(() => setSuccessMessage(''), 5000);
        setShowPriceConfirm(null);

        if (modalRef.current) modalRef.current.scrollTop = scrollTop;
    };

    const handleVeggieSubstitution = (orderIndex, targetVeggieId, isCurrentlyActive) => {
        const scrollTop = modalRef.current?.scrollTop || 0;
        const order = orders[orderIndex];
        const targetVeggie = order.veggieOptions?.find(opt => opt.id === targetVeggieId);

        // Update local state - toggle veggie active status
        const updatedVeggieOptions = order.veggieOptions?.map(opt => ({
            ...opt,
            isActive: opt.id === targetVeggieId ? !isCurrentlyActive : opt.isActive
        }));

        // Update finalIngredients (maintain {id, name} structure)
        let updatedFinalIngredients = [...(order.finalIngredients || [])];
        if (isCurrentlyActive) {
            // Remove veggie
            updatedFinalIngredients = updatedFinalIngredients.filter(ing => ing.id !== targetVeggieId);
        } else {
            // Add veggie
            if (!updatedFinalIngredients.some(ing => ing.id === targetVeggieId)) {
                updatedFinalIngredients.push({ id: targetVeggieId, name: targetVeggie?.name || '' });
            }
        }

        const updatedOrder = {
            ...order,
            veggieOptions: updatedVeggieOptions,
            finalIngredients: updatedFinalIngredients,
            hasUnsavedChange: true
        };

        const updatedOrders = [...orders];
        updatedOrders[orderIndex] = updatedOrder;

        // Track the change
        trackPendingChange(selectedDeliveryDate, {
            type: 'TOGGLE_VEGGIE',
            recordId: order.recordId,
            dishName: order.itemName,
            veggieId: targetVeggieId,
            veggieName: targetVeggie?.name,
            action: isCurrentlyActive ? 'remove' : 'add',
            timestamp: Date.now(),
            monetaryChange: false
        });

        flushSync(() => updateOrdersState(updatedOrders));
        // setSuccessMessage(`Veggie ${isCurrentlyActive ? 'removed' : 'added'}! Remember to "Save Changes" in Order Summary.`);
        // setTimeout(() => setSuccessMessage(''), 5000);

        if (modalRef.current) modalRef.current.scrollTop = scrollTop;
    };

    const handleSauceSubstitution = (orderIndex, newSauceId, oldSauceId) => {
        const scrollTop = modalRef.current?.scrollTop || 0;
        const order = orders[orderIndex];
        const oldSauce = order.sauceOptions?.find(opt => opt.id === oldSauceId);
        const newSauce = order.sauceOptions?.find(opt => opt.id === newSauceId);

        // Update local state - update sauce options to reflect new selection
        const updatedSauceOptions = order.sauceOptions?.map(opt => ({
            ...opt,
            isActive: opt.id === newSauceId
        }));

        // Update finalIngredients - replace old sauce with new (maintain {id, name} structure)
        let updatedFinalIngredients = [...(order.finalIngredients || [])];
        // Remove all sauce options first
        const allSauceIds = order.sauceOptions?.map(opt => opt.id) || [];
        updatedFinalIngredients = updatedFinalIngredients.filter(ing => !allSauceIds.includes(ing.id));
        // Add new sauce if selected
        if (newSauceId) {
            updatedFinalIngredients.push({ id: newSauceId, name: newSauce?.name || '' });
        }

        const updatedOrder = {
            ...order,
            sauceOptions: updatedSauceOptions,
            finalIngredients: updatedFinalIngredients,
            hasUnsavedChange: true
        };

        const updatedOrders = [...orders];
        updatedOrders[orderIndex] = updatedOrder;

        // Track the change
        trackPendingChange(selectedDeliveryDate, {
            type: 'REPLACE_SAUCE',
            recordId: order.recordId,
            dishName: order.itemName,
            newSauceId,
            newSauceName: newSauce?.name || '',
            oldSauceId,
            oldSauceName: oldSauce?.name || '',
            timestamp: Date.now(),
            monetaryChange: false
        });

        flushSync(() => updateOrdersState(updatedOrders));
        // setSuccessMessage('Sauce updated! Remember to "Save Changes" in Order Summary.');
        // setTimeout(() => setSuccessMessage(''), 5000);

        if (modalRef.current) modalRef.current.scrollTop = scrollTop;
    };

    const handleStarchSubstitution = (orderIndex, newStarchId, oldStarchId) => {
        const scrollTop = modalRef.current?.scrollTop || 0;
        const order = orders[orderIndex];
        const oldStarch = order.starchOptions?.find(opt => opt.id === oldStarchId);
        const newStarch = order.starchOptions?.find(opt => opt.id === newStarchId);

        // Update local state - update starch options to reflect new selection
        const updatedStarchOptions = order.starchOptions?.map(opt => ({
            ...opt,
            isActive: opt.id === newStarchId
        }));

        // Update finalIngredients - replace old starch with new (maintain {id, name} structure)
        let updatedFinalIngredients = [...(order.finalIngredients || [])];
        // Remove all starch options first
        const allStarchIds = order.starchOptions?.map(opt => opt.id) || [];
        updatedFinalIngredients = updatedFinalIngredients.filter(ing => !allStarchIds.includes(ing.id));
        // Add new starch if selected
        if (newStarchId) {
            updatedFinalIngredients.push({ id: newStarchId, name: newStarch?.name || '' });
        }

        const updatedOrder = {
            ...order,
            starchOptions: updatedStarchOptions,
            finalIngredients: updatedFinalIngredients,
            hasUnsavedChange: true
        };

        const updatedOrders = [...orders];
        updatedOrders[orderIndex] = updatedOrder;

        // Track the change
        trackPendingChange(selectedDeliveryDate, {
            type: 'REPLACE_STARCH',
            recordId: order.recordId,
            dishName: order.itemName,
            timestamp: Date.now(),
            newStarchId,
            newStarchName: newStarch?.name || '',
            oldStarchId,
            oldStarchName: oldStarch?.name || '',
            monetaryChange: false
        });

        flushSync(() => updateOrdersState(updatedOrders));
        // setSuccessMessage('Starch updated! Remember to "Save Changes" in Order Summary.');
        // setTimeout(() => setSuccessMessage(''), 5000);

        if (modalRef.current) modalRef.current.scrollTop = scrollTop;
    };

    const handleToggleGarnish = (orderIndex, garnishId, isCurrentlyActive) => {
        const scrollTop = modalRef.current?.scrollTop || 0;
        const order = orders[orderIndex];
        const targetGarnish = order.garnishOptions?.find(opt => opt.id === garnishId);

        // Update local state - toggle garnish active status
        const updatedGarnishOptions = order.garnishOptions?.map(opt => ({
            ...opt,
            isActive: opt.id === garnishId ? !isCurrentlyActive : opt.isActive
        }));

        // Update finalIngredients (maintain {id, name} structure)
        let updatedFinalIngredients = [...(order.finalIngredients || [])];
        if (isCurrentlyActive) {
            // Remove garnish
            updatedFinalIngredients = updatedFinalIngredients.filter(ing => ing.id !== garnishId);
        } else {
            // Add garnish
            if (!updatedFinalIngredients.some(ing => ing.id === garnishId)) {
                updatedFinalIngredients.push({ id: garnishId, name: targetGarnish?.name || '' });
            }
        }

        const updatedOrder = {
            ...order,
            garnishOptions: updatedGarnishOptions,
            finalIngredients: updatedFinalIngredients,
            hasUnsavedChange: true
        };

        const updatedOrders = [...orders];
        updatedOrders[orderIndex] = updatedOrder;

        // Track the change
        trackPendingChange(selectedDeliveryDate, {
            type: 'TOGGLE_GARNISH',
            recordId: order.recordId,
            dishName: order.itemName,
            garnishId,
            action: isCurrentlyActive ? 'remove' : 'add',
            monetaryChange: false
        });

        flushSync(() => updateOrdersState(updatedOrders));
        // setSuccessMessage(`Garnish ${isCurrentlyActive ? 'removed' : 'added'}! Remember to "Save Changes" in Order Summary.`);
        // setTimeout(() => setSuccessMessage(''), 5000);

        if (modalRef.current) modalRef.current.scrollTop = scrollTop;
    };

    // Render loading state
    if (loading) {
        return (
            <CountdownLoader
                duration={15}
                onComplete={() => setLoading(false)}
            />
        );
    }

    // Render error state
    if (error) {
        return (
            <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px' }}>
                <div style={{
                    backgroundColor: '#fee',
                    border: '1px solid #fcc',
                    borderRadius: '8px',
                    padding: '20px'
                }}>
                    <h2 style={{ color: '#c33', marginBottom: '10px' }}>Error</h2>
                    <p style={{ color: '#c33' }}>{error}</p>
                    <button
                        onClick={fetchMealPlan}
                        style={{
                            marginTop: '15px',
                            padding: '10px 20px',
                            backgroundColor: '#c33',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer'
                        }}
                    >
                        Go Back to Meal Plan
                    </button>
                </div>
            </div>
        );
    }

    // Wait for data to load before rendering main content
    if (!customerDetails) {
        return null;
    }

    const customer = customerDetails.customer;
    const subscriptions = customerDetails.subscriptions;

    return (
        <div className="meal-plan-layout" style={{ backgroundColor: 'rgba(255,252,248,255)' }}>
            <div className="meal-plan-content">

                <MealPlanHeader customer={customer} />
                <SuccessMessage
                    message={successMessage}
                    onClose={() => setSuccessMessage('')}
                />
                <ErrorMessage
                    message={error}
                    onClose={() => setError(null)}
                />

                <DeliveryDateTabs
                    customerDetails={customerDetails}
                    deliveryDates={deliveryDates}
                    selectedDeliveryDate={selectedDeliveryDate}
                    ordersByDeliveryDate={ordersByDeliveryDate}
                    deliveryStatusState={deliveryStatusState}
                    onDeliveryDateChange={handleDeliveryDateChange}
                    customerToken={token}
                    isLocked={isLocked}
                    onSkipConfirmed={(selectedNextDeliveryDate) => {
                        // Process orders - one-time operation when skip is confirmed
                        const currentOrders = ordersByDeliveryDate[selectedDeliveryDate] || [];
                        const processedOrders = currentOrders
                            .filter(order => order.isCore) // Keep only core meals
                            .map(order => ({
                                ...order,
                                isOrdered: false,  // Mark as not ordered
                                quantity: 0        // Reset quantity
                            }));

                        // Update orders for this delivery date
                        setOrdersByDeliveryDate(prev => ({
                            ...prev,
                            [selectedDeliveryDate]: processedOrders
                        }));

                        // Update current view
                        setOrders(processedOrders);
                        flushSync(() => {
                            setDeliveryStatusState(prev => {
                                const newState = {
                                    ...prev,
                                    [selectedDeliveryDate]: {
                                        ...prev[selectedDeliveryDate],
                                        isSkipped: true,
                                        nextDeliveryDate: selectedNextDeliveryDate
                                    }
                                };
                                return newState;
                            });
                        });

                        // Clear any pending changes for this skipped date
                        clearPendingChanges(selectedDeliveryDate);

                        setSuccessMessage(`Delivery skipped! Next delivery date is ${selectedNextDeliveryDate}`);
                        setTimeout(() => setSuccessMessage(''), 10000);
                    }}
                />

                {/* Resume Delivery Banner - only show if delivery is skipped */}
                {deliveryStatusState[selectedDeliveryDate]?.isSkipped && !isLocked && (
                    <ResumeDelivery
                        selectedDeliveryDate={selectedDeliveryDate}
                        customerToken={token}
                        nextDeliveryDate={deliveryStatusState[selectedDeliveryDate]?.nextDeliveryDate}
                        onResumeConfirmed={() => {
                            // Immediately update this date's status to not skipped
                            flushSync(() => {
                                setDeliveryStatusState(prev => {
                                    const newState = {
                                        ...prev,
                                        [selectedDeliveryDate]: {
                                            ...prev[selectedDeliveryDate],
                                            isSkipped: false,
                                            nextDeliveryDate: null
                                        }
                                    };

                                    return newState;
                                });
                            });
                            setSuccessMessage(`${selectedDeliveryDate} delivery resumed! You can now add meals to your order.`);
                            setTimeout(() => setSuccessMessage(''), 8000);
                        }}
                    />
                )}

                <LockMessage isLocked={isLocked} />
                {/* Meal Orders */}
                <div ref={containerRef} style={{ padding: '0px 20px 20px 20px', position: 'relative' }}>

                    {/* Group orders by meal type */}
                    {MEAL_TYPES.map(mealType => {
                        const mealOrders = orders.filter(order => order.meal === mealType);
                        const scrollState = getScrollState(mealType);

                        if (mealOrders.length === 0) return null;

                        const subscriptionCount = getSubscriptionCount(mealType);
                        const orderedCount = getOrderedMealCount(mealType, selectedDeliveryDate);


                        return (
                            <div key={mealType} style={{ marginBottom: '30px' }}>
                                <h4 style={{
                                    color: '#333',
                                    marginBottom: '15px',
                                    paddingBottom: '5px',
                                    borderBottom: '2px solid #eee'
                                }}>
                                    {mealType}
                                    <span style={{
                                        fontSize: '14px',
                                        color: '#666',
                                        fontWeight: 'normal',
                                        marginLeft: '8px'
                                    }}>
                                        {getPriceText(mealType, subscriptionCount, orderedCount)}
                                    </span>
                                </h4>

                                <div style={{ position: 'relative' }}>
                                    {/* Scrolling container */}
                                    <div
                                        data-meal-type={mealType}
                                        onScroll={(e) => handleScroll(mealType, e)}
                                        style={{
                                            display: 'flex',
                                            overflowX: 'auto',
                                            gap: '15px',
                                            paddingBottom: '10px'
                                        }}
                                    >
                                        {mealOrders.map((order) => {
                                            const actualIndex = orders.indexOf(order);
                                            return (
                                                <CompactMealCard
                                                    key={order.recordId || actualIndex}
                                                    meal={order}
                                                    actualIndex={actualIndex}
                                                    onOpenModal={openModal}
                                                    onQuantityChange={handleQuantityChange}
                                                    isLocked={isLocked || deliveryStatusState[selectedDeliveryDate]?.isSkipped}
                                                    saving={saving}
                                                />
                                            );
                                        })}
                                    </div>

                                    <ScrollArrows
                                        mealType={mealType}
                                        shouldShowArrows={shouldShowArrows}
                                        itemCount={mealOrders.length}
                                        scrollState={scrollState}
                                        onScrollLeft={scrollLeft}
                                        onScrollRight={scrollRight}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Modal Components */}
                {showModal && selectedMeal && (
                    <MealModal
                        meal={selectedMeal.actualIndex !== undefined ?
                            { ...orders[selectedMeal.actualIndex], actualIndex: selectedMeal.actualIndex } :
                            selectedMeal
                        }
                        onClose={closeModal}
                        modalRef={modalRef}
                        saving={saving}
                        // Pass all the substitution handlers as props to the modal
                        onProteinSubstitution={handleProteinSubstitution}
                        onVeggieSubstitution={handleVeggieSubstitution}
                        onSauceSubstitution={handleSauceSubstitution}
                        onStarchSubstitution={handleStarchSubstitution}
                        onToggleGarnish={handleToggleGarnish}
                        isLocked={isLocked || deliveryStatusState[selectedDeliveryDate]?.isSkipped}
                        showAddedBanner={showAddedBanner}
                        // Pass billing changes which includes premium usage data
                        billingChanges={billingChanges}
                        deliveryDates={deliveryDates}
                        selectedDeliveryDate={selectedDeliveryDate}
                    />
                )}

                {showPriceConfirm && (
                    <PremiumProteinUpgradeModal
                        confirmData={showPriceConfirm}
                        onConfirm={() => {
                            executeProteinSubstitution(
                                showPriceConfirm.orderIndex,
                                showPriceConfirm.newProteinId,
                                showPriceConfirm.oldProteinId,
                                showPriceConfirm.price
                            );
                        }}
                        onCancel={() => setShowPriceConfirm(null)}
                    />
                )}

                {/* Summary Modal - only for tablet & mobile */}
                {showSummaryModal && !isDesktop && (
                    <SummaryModal
                        ordersByDeliveryDate={ordersByDeliveryDate}
                        deliveryDates={deliveryDates}
                        billingChanges={billingChanges}
                        customerToken={token}
                        pendingChanges={pendingChanges}
                        deliveryTimeOverrides={deliveryTimeOverrides}
                        onDeliveryTimeChange={handleDeliveryTimeChange}
                        priorPaymentsByDate={priorPaymentsByDate}
                        paymentRecords={paymentRecords}
                        onSaveSuccess={(date, results, allSucceeded) => {
                            updateOrderRecordIds(date, results);
                            if (allSucceeded) {
                                clearPendingChanges(date);
                            } else {
                                const successfulChangeIds = results
                                    .filter(r => r.success && r.changeId)
                                    .map(r => r.changeId);
                                removeSuccessfulChanges(date, successfulChangeIds);
                            }
                        }}
                        onClose={() => setShowSummaryModal(false)}
                        customerDetails={customerDetails}
                    />
                )}
            </div>

            {/* Desktop: Fixed sidebar */}
            {isDesktop && (
                <OrderSummarySidebar
                    ordersByDeliveryDate={ordersByDeliveryDate}
                    deliveryDates={deliveryDates}
                    billingChanges={billingChanges}
                    customerToken={token}
                    pendingChanges={pendingChanges}
                    deliveryTimeOverrides={deliveryTimeOverrides}
                    onDeliveryTimeChange={handleDeliveryTimeChange}
                    priorPaymentsByDate={priorPaymentsByDate}
                    paymentRecords={paymentRecords}
                    onSaveSuccess={(date, results, allSucceeded) => {
                        updateOrderRecordIds(date, results);
                        if (allSucceeded) {
                            clearPendingChanges(date);
                        } else {
                            const successfulChangeIds = results
                                .filter(r => r.success && r.changeId)
                                .map(r => r.changeId);
                            removeSuccessfulChanges(date, successfulChangeIds);
                        }
                    }}
                    customerDetails={customerDetails}
                />
            )}

            {/* Tablet & Mobile: Floating button */}
            {!isDesktop && (
                <FloatingOrderButton onClick={() => setShowSummaryModal(true)} />
            )}

            {/* Payment processing modal overlay */}
            {paymentProcessingModal && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 3000
                }}>
                    <div style={{
                        backgroundColor: '#fff',
                        borderRadius: '12px',
                        padding: '32px 24px',
                        maxWidth: '420px',
                        width: '90%',
                        textAlign: 'center',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.25)'
                    }}>
                        {paymentProcessingModal === 'processing' && (
                            <>
                                <div style={{ fontSize: '36px', marginBottom: '16px' }}>&#8987;</div>
                                <h3 style={{ margin: '0 0 8px', color: '#333', fontSize: '20px' }}>Processing Payment</h3>
                                <p style={{ color: '#666', fontSize: '15px', margin: 0 }}>Applying your changes, please wait...</p>
                            </>
                        )}
                        {paymentProcessingModal === 'success' && (
                            <>
                                <div style={{ fontSize: '36px', marginBottom: '16px' }}>&#10003;</div>
                                <h3 style={{ margin: '0 0 8px', color: '#28a745', fontSize: '20px' }}>Payment Successful</h3>
                                <p style={{ color: '#666', fontSize: '15px', margin: '0 0 20px' }}>Your changes have been saved.</p>
                                <button
                                    onClick={() => setPaymentProcessingModal(null)}
                                    style={{
                                        padding: '10px 32px',
                                        backgroundColor: '#ff7c44',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >OK</button>
                            </>
                        )}
                        {paymentProcessingModal === 'failed' && (
                            <>
                                <div style={{ fontSize: '36px', marginBottom: '16px' }}>&#9888;</div>
                                <h3 style={{ margin: '0 0 8px', color: '#333', fontSize: '20px' }}>Payment Received</h3>
                                <p style={{ color: '#666', fontSize: '15px', margin: '0 0 20px' }}>We ran into an issue applying your changes — our team has been notified and will take care of it shortly. No action needed on your end.</p>
                                <button
                                    onClick={() => setPaymentProcessingModal(null)}
                                    style={{
                                        padding: '10px 32px',
                                        backgroundColor: '#ff7c44',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >OK</button>
                            </>
                        )}
                        {paymentProcessingModal === 'timeout' && (
                            <>
                                <div style={{ fontSize: '36px', marginBottom: '16px' }}>&#9888;</div>
                                <h3 style={{ margin: '0 0 8px', color: '#333', fontSize: '20px' }}>Payment Received</h3>
                                <p style={{ color: '#666', fontSize: '15px', margin: '0 0 20px' }}>Your changes are being processed. Please refresh in a few minutes if they don't appear. <br />Contact us at <a href="mailto:team@getsquarefare.com" style={{ color: '#ff7c44' }}>team@getsquarefare.com</a> if you need help.</p>
                                <button
                                    onClick={() => setPaymentProcessingModal(null)}
                                    style={{
                                        padding: '10px 32px',
                                        backgroundColor: '#ff7c44',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '1px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >OK</button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}